import { Cake } from 'lucide-react';
import {
  DAY_PART_LABELS,
  birthdayHeadline,
  dayPartOf,
  formatTime,
  isSameDay,
  shortDate,
  weekdayName,
  type DayPart,
  type EventOccurrence,
  type UpcomingBirthday,
} from '@awwyeah/shared';
import type { MemberView } from '../members';
import { EventPill } from './EventPill';
import { MemberStack } from './MemberBits';

const PART_ORDER: DayPart[] = ['morning', 'afternoon', 'evening'];

export interface DayAgendaProps {
  day: Date;
  now: Date;
  occurrences: readonly EventOccurrence[];
  birthdays: readonly UpcomingBirthday[];
  members: ReadonlyMap<string, MemberView>;
  currentUserId: string | null;
  onOpenOccurrence: (occurrence: EventOccurrence) => void;
  onAdd: () => void;
}

/**
 * Day / agenda (design Screen C · 6): morning / afternoon / evening rules instead of empty
 * hour rows, an ember "now" rule on today, and a closing line about what's left.
 */
export function DayAgenda({
  day,
  now,
  occurrences,
  birthdays,
  members,
  currentUserId,
  onOpenOccurrence,
  onAdd,
}: DayAgendaProps): JSX.Element {
  const isToday = isSameDay(day, now);
  const allDay = occurrences.filter((occurrence) => occurrence.event.allDay);
  const timed = occurrences.filter((occurrence) => !occurrence.event.allDay);
  const mine = currentUserId
    ? occurrences.filter((occurrence) => occurrence.event.ownerIds.includes(currentUserId)).length
    : 0;
  const ownerIds = [...new Set(occurrences.flatMap((occurrence) => occurrence.event.ownerIds))];
  const nowPart = dayPartOf(now);
  const lastEnd = timed.reduce(
    (latest, occurrence) => Math.max(latest, Date.parse(occurrence.end)),
    0,
  );

  return (
    <section className="flex flex-col gap-2" aria-label="Day agenda">
      <header className="flex flex-col gap-1 pb-2">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display text-[28px] leading-none tracking-tight">
            {weekdayName(day)}
          </h2>
          <span className="text-base text-fg-3">{shortDate(day, now)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[13px] text-fg-3">
            {occurrences.length === 0
              ? 'Nothing on the books'
              : `${occurrences.length} thing${occurrences.length === 1 ? '' : 's'}${mine > 0 ? ` · ${mine} of them yours` : ''}`}
          </span>
          <MemberStack ownerIds={ownerIds} byId={members} />
        </div>
      </header>

      {birthdays.map((entry) => (
        <div
          key={entry.birthday.id}
          className="flex items-center gap-2 rounded-sm border border-line bg-highlight px-3 py-2 text-sm font-semibold"
        >
          <Cake className="h-4 w-4 text-accent" aria-hidden="true" />
          {birthdayHeadline(entry)}
        </div>
      ))}

      {allDay.length > 0 && (
        <div className="flex flex-col gap-2">
          {allDay.map((occurrence) => (
            <EventPill
              key={occurrence.key}
              occurrence={occurrence}
              members={members}
              onOpen={onOpenOccurrence}
            />
          ))}
        </div>
      )}

      {occurrences.length === 0 ? (
        <div className="flex flex-col items-start gap-3 border-t-2 border-line-strong py-6">
          <p className="font-display text-xl">Nothing here yet</p>
          <p className="text-sm text-fg-2">
            {isToday
              ? "Today's wide open. Either it's a quiet one or somebody hasn't told the rest of us."
              : 'Nothing planned for this day.'}
          </p>
          <button
            type="button"
            onClick={onAdd}
            data-testid="empty-add-button"
            className="flex h-11 items-center rounded-md bg-inverse px-4 text-sm font-semibold text-inverse-fg"
          >
            Add something
          </button>
          <span className="text-xs text-fg-3">
            Adding works fine offline — it&apos;ll sync when you&apos;re back.
          </span>
        </div>
      ) : (
        PART_ORDER.map((part) => {
          const inPart = timed.filter((occurrence) => dayPartOf(occurrence.start) === part);
          if (inPart.length === 0) return null;
          return (
            <div key={part} className="flex flex-col gap-2 pt-1">
              <Rule label={DAY_PART_LABELS[part]} />
              {inPart.map((occurrence) => (
                <div key={occurrence.key} className="flex flex-col gap-2">
                  <EventPill occurrence={occurrence} members={members} onOpen={onOpenOccurrence} />
                </div>
              ))}
              {isToday && part === nowPart && <Rule label={`Now · ${formatTime(now)}`} accent />}
            </div>
          );
        })
      )}

      {timed.length > 0 && lastEnd > 0 && (
        <p className="pt-3 text-xs text-fg-3">Nothing after {formatTime(new Date(lastEnd))}.</p>
      )}
    </section>
  );
}

function Rule({ label, accent = false }: { label: string; accent?: boolean }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${accent ? 'text-accent' : 'text-fg-3'}`}
      >
        {label}
      </span>
      <span className={`flex-1 border-t ${accent ? 'border-t-2 border-accent' : 'border-line'}`} />
    </div>
  );
}
